import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import CustomDataGrid from '../../components/data-grid';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import {
  checkUserAuthenticated, formatDateForDataGrid, getClientApiKeys, goToRoutePage, setWindowTitle
} from '../../utils/helper';
import { AddIcon, RefreshIcon } from '../../utils/icons';
import {
  Box, Breadcrumbs, Grid, IconButton, Link, Tooltip, Typography
} from '../../utils/material-ui-core';
import ApiKeyCURD from './crud/apikey-crud';

const ManageApiKey = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { isUserAuthenticated, userFunctionAccess, showAlert, referenceLookups } = useContext(AppContext);

  const [loading, setLoading] = useState<boolean>(false);
  const [openEditDialogOpen, setOpenEditDialogOpen] = useState<boolean>(false);
  const [selectedApiKey, setSelectedApiKey] = useState("");

  const [clientApiKeys, setClientApiKeys] = useState<ClientApiKey[]>([]);

  const columns = [
    { field: "clientCompanyName", headerName: "Client Company", width: 300 },
    { field: "firstName", headerName: "First Name", width: 300 },
    { field: "lastName", headerName: "Last Name", width: 300 },
    { field: "email", headerName: "Email", width: 350 },
    { field: "note", headerName: "Note", width: 400 },
    { field: "lastModifiedBy", headerName: "Last Modified By", width: 300 },
    { field: "lastModifiedDateTime", headerName: "Last Modified DateTime", width: 200, type: "date", valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "apiKey", headerName: "Key", width: 350, hide: true },
  ];

  const handleDataGridClick = (params: GridRowParams, event: any) => {
    setSelectedApiKey(params.row.apiKey);
    setOpenEditDialogOpen(true);
  };

  const newApiKey = () => {
    setSelectedApiKey(referenceLookups.newRecordDefaultId);
    setOpenEditDialogOpen(true);
  };

  const refresh = async () => {
    setLoading(true);
    await getData();
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  const getData = async () => {
    const apiResponse = await getClientApiKeys();
    if (apiResponse.success) {
      const data = apiResponse.data.clientApiKeys as ClientApiKey[];
      const rows: any = [];
      data.map((apikeyItem, index) => rows.push({ id: index, ...apikeyItem }));
      setClientApiKeys(rows as ClientApiKey[]);
    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      return;
    }
  };

  const saveConfirmation = (confirmOutput: ConfirmOutput) => {
    setOpenEditDialogOpen(false);
    if (confirmOutput.status === "refresh") {
      refresh();
    }
  };

  useEffect(() => {
    setWindowTitle("Admin Users");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);
      if (isUserAuthenticated) {
        setLoading(false);
      }

      if (!userFunctionAccess.userPermissionAdmin) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      await getData();
    };
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated, userFunctionAccess]);

  return (
    <React.Fragment>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <ApiKeyCURD open={openEditDialogOpen} apiKey={selectedApiKey} selectedClientCompanyKey={""} onCallback={saveConfirmation} />

          <Box className={pageStyles.root}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={globalStyles.bottomSpacer}>
                  <Breadcrumbs aria-label="breadcrumb">
                    <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                      Home
                    </Link>
                    <Link color="inherit" onClick={() => goToRoutePage("managesettings")} className={globalStyles.link}>
                      Manage Settings
                    </Link>
                    <Typography color="textPrimary">Api Keys</Typography>
                  </Breadcrumbs>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box display="flex" flexDirection="row-reverse">
                  {userFunctionAccess.userPermissionAdmin ? (
                    <IconButton onClick={() => newApiKey()}>
                      <Tooltip arrow title={"Add new api key"}>
                        <AddIcon color="secondary" />
                      </Tooltip>
                    </IconButton>
                  ) : null}

                  <IconButton onClick={() => refresh()}>
                    <Tooltip arrow title={"Refresh"}>
                      <RefreshIcon color="secondary" />
                    </Tooltip>
                  </IconButton>
                </Box>
              </Grid>
            </Grid>

            <Box className={pageStyles.datagridContainer}>
              <CustomDataGrid uniqueGridCode={"ApiKeyListResult"} columns={columns} rows={clientApiKeys} showToolBar={true} sortModel={[{ field: "lastModifiedDateTime", sort: "desc" }]} handleDataGridClick={handleDataGridClick} />
            </Box>
          </Box>
        </Screen>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    datagridContainer: {
      height: "70vh",
      width: "100%",
      marginBottom: theme.spacing(2),
    },
  })
);

export default ManageApiKey;
